import { handleActions } from 'redux-actions';
import {
  createTimeslotSuccess,
  createTimeslotFailure,
  deleteTimeslotSuccess,
  toggleTimeslotSettings,
  setSelectedTimeslot,
  setSelectedResource,
  clearValidationErrors,
  hideMenus,
  setMenuIsOpen,
  setRule,
  deleteRule,
  toggleDeleteModal,
  setReservationsExists,
  fetchTimeslots,
  setNewExceptionRule,
  setNewExceptionDate,
  setNewExceptionHoursOnDate,
  deleteExceptions,
  toggleAppointmentModal,
  toggleBookingModal,
  toggleBookingCancelModal,
  setAppointmentsAmount,
  startOverwriting,
  finishOverwriting,
  deleteTimeslotFailure,
  setCurrentAppointment,
  setIsDayPage,
  setIsSlotClickFromCell,
} from './actions';
import {
  getRuleDescription,
  checkRecurrenceRule,
} from "../../services/helpers";
// Cross-domain action type (string constant only — importing the auth action
// creators would drag firebase into this module).
import { LOGIN_SUCCESS } from '../auth/actionTypes';

const initialState = {
  fetching: false,
  errors: {
    startTimeError: '',
    endTimeError: '',
    bloodTypeError: '',
    differenceTimeError: '',
    durationTimeError: '',
    backendError: '',
  },
  settingsModalIsOpen: false,
  selectedTimeslot: null,
  selectedResourceId: null,
  selectedTime: null,
  timeslotMenuIsOpened: false,
  recurrenceRule: null,
  ruleDescription: '',
  deleteTimeslotModalIsOpen: false,
  bookedReservations: false,
  isTimeslotsFetching: false,
  isTimeslotSuccessToastShown: false,
  isTimeslotErrorToastShown: false,
  exceptionRules: [],
  exceptionDates: [],
  exceptionHoursOnDates: [],
  deleteAppointmentModalIsOpen: false,
  bookAppointmentModalIsOpen: false,
  bookingCancelModalIsOpen: false,
  appointmentsAmount: null,
  isTimeslotOverwriting: false,
  currentAppointment: null,
  isDayPage: true,
  isSlotClickFromCell: false,
};

const timeslotsReducer = handleActions({
  // Auth (re)established — a leftover error toast flag from a previous auth
  // state must not fire Schedule's mount-time toast after a fresh sign-in
  // (same reasoning as the reservations reducer's LOGIN_SUCCESS reset).
  [LOGIN_SUCCESS]: (state) => ({
    ...state,
    isTimeslotErrorToastShown: false,
  }),
  [createTimeslotSuccess]: (state) => ({
    ...state,
    errors: {
      startTimeError: '',
      endTimeError: '',
      bloodTypeError: '',
      differenceTimeError: '',
      durationTimeError: '',
    },
    isTimeslotsFetching: false,
    isTimeslotSuccessToastShown: true,
    isTimeslotErrorToastShown: false,
    recurrenceRule: null,
    ruleDescription: ''
  }),
  [createTimeslotFailure]: (state, action) => ({
    ...state,
    errors: action.payload.errors,
    isTimeslotErrorToastShown: true,
    isTimeslotSuccessToastShown: false,
    isTimeslotsFetching: false,
  }),
  [deleteTimeslotSuccess]: (state) => ({
    ...state,
    isTimeslotOverwriting: false,
  }),
  [deleteTimeslotFailure]: (state) => ({
    ...state,
    isTimeslotOverwriting: false,
  }),
  [toggleTimeslotSettings]: (state) => ({
    ...state,
    settingsModalIsOpen: !state.settingsModalIsOpen,
    errors: {
      startTimeError: '',
      endTimeError: '',
      bloodTypeError: '',
      differenceTimeError: '',
      durationTimeError: '',
      backendError: '',
    },
    bookedReservations: null,
  }),
  [setSelectedResource]: (state, action) => ({
    ...state,
    selectedResourceId: action.payload.resourceId,
    selectedTime: action.payload.selectedTime,
    selectedTimeslot: null,
    recurrenceRule: null,
    ruleDescription: '',
    appointmentsAmount: null
  }),
  [setSelectedTimeslot]: (state, action) => ({
    ...state,
    selectedTimeslot: action.payload,
    recurrenceRule: checkRecurrenceRule(action.payload?.initialSettings) ? action.payload?.initialSettings : null,
    exceptionRules: action.payload?.initialExceptionRules || [],
    exceptionDates: action.payload?.initialExceptionDates || [],
    exceptionHoursOnDates: action.payload?.initialExceptionHoursOnDates || [],
    ruleDescription: action.payload?.ruleDescription,
    selectedResourceId: null,
    selectedTime: null,
    bookedReservations: null,
  }),
  [clearValidationErrors]: (state) => ({
    ...state,
    errors: {
      startTimeError: '',
      endTimeError: '',
      bloodTypeError: '',
      differenceTimeError: '',
      durationTimeError: '',
      backendError: '',
    },
  }),
  [hideMenus]: (state) => ({
    ...state,
    timeslotMenuIsOpened: false,
  }),
  [setMenuIsOpen]: (state) => ({
    ...state,
    timeslotMenuIsOpened: true,
  }),
  [setRule]: (state, action) => ({
    ...state,
    recurrenceRule: action.payload,
    ruleDescription: getRuleDescription(action.payload),
  }),
  [deleteRule]: (state) => ({
    ...state,
    recurrenceRule: null,
    ruleDescription: '',
  }),
  [toggleDeleteModal]: (state) => ({
    ...state,
    deleteTimeslotModalIsOpen: !state.deleteTimeslotModalIsOpen,
  }),
  [setReservationsExists]: (state, action) => ({
    ...state,
    isTimeslotsFetching: false,
    bookedReservations: action.payload.bookedReservations,
  }),
  [fetchTimeslots]: (state) => ({
    ...state,
    isTimeslotsFetching: true
  }),
  [setNewExceptionRule]: (state, action) => ({
    ...state,
    exceptionRules: action.payload,
  }),
  [setNewExceptionDate]: (state, action) => ({
    ...state,
    exceptionDates: action.payload,
  }),
  [setNewExceptionHoursOnDate]: (state, action) => ({
    ...state,
    exceptionHoursOnDates: action.payload,
  }),
  [deleteExceptions]: (state) => ({
    ...state,
    exceptionRules: [],
    exceptionDates: [],
    exceptionHoursOnDates: [],
  }),
  [toggleAppointmentModal]: (state) => ({
    ...state,
    deleteAppointmentModalIsOpen: !state.deleteAppointmentModalIsOpen,
  }),
  [toggleBookingModal]: (state) => ({
    ...state,
    bookAppointmentModalIsOpen: !state.bookAppointmentModalIsOpen,
  }),
  [toggleBookingCancelModal]: (state) => ({
    ...state,
    bookingCancelModalIsOpen: !state.bookingCancelModalIsOpen,
  }),
  [setAppointmentsAmount]: (state, action) => ({
    ...state,
    appointmentsAmount: action.payload,
  }),
  [startOverwriting]: (state) => ({
    ...state,
    isTimeslotOverwriting: true,
  }),
  [finishOverwriting]: (state) => ({
    ...state,
    isTimeslotOverwriting: false,
  }),
  [setCurrentAppointment]: (state, action) => ({
    ...state,
    currentAppointment: action.payload,
  }),
  [setIsDayPage]: (state, action) => ({
    ...state,
    isDayPage: action.payload
  }),
  [setIsSlotClickFromCell]: (state, action) => ({
    ...state,
    isSlotClickFromCell: action.payload
  }),
}, initialState);

export default timeslotsReducer;
